import {
  Badge,
  Box,
  Button,
  Card as ChakraCard,
  CardBody,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Card from 'components/card/Card';
import { queryClient } from 'index';
import { deleteProduct, findProducts, getProductGroup, Product } from 'services/api.service';
import GroupFormModal from '../groups/components/GroupFormModal';
import ProductFormModal from './components/ProductFormModal';
import { maskCurrency } from 'utils/number';

export default function CatalogGroupProducts() {
  const toast = useToast();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isGroupOpen, onOpen: onGroupOpen, onClose: onGroupClose } = useDisclosure();
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  const { data: group } = useQuery(['productGroup', groupId], () => getProductGroup(groupId!));
  const { data: products, isLoading } = useQuery(['products', groupId], () => findProducts(groupId));

  const openCreate = () => {
    setEditingId(undefined);
    onOpen();
  };
  const openEdit = (id: string) => {
    setEditingId(id);
    onOpen();
  };
  const remove = async (id: string) => {
    if (!window.confirm('Remover este produto?')) return;
    try {
      await deleteProduct(id);
      await queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({ title: 'Produto removido', status: 'success', duration: 4000, isClosable: true });
    } catch (err: any) {
      toast({
        title: 'Erro',
        description: err?.response?.data?.message || 'Erro ao remover',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatPrice = (price: number) => maskCurrency(price);
  const sumPct = (p: Product) => (p.splits || []).reduce((a, s) => a + Number(s.percentage), 0);

  return (
    <Box>
      <Card>
        <Flex
          justify="space-between"
          align="center"
          mb={4}
          wrap="wrap"
          gap={2}
        >
          <Flex
            align="center"
            gap={3}
            wrap="wrap"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/access/catalog/groups')}
            >
              Voltar
            </Button>
            <Box
              fontSize="lg"
              fontWeight="bold"
            >
              {group?.name || 'Produtos do grupo'}
            </Box>
          </Flex>
          <Flex
            gap={2}
            align="center"
          >
            <Button
              size="sm"
              variant="outline"
              onClick={onGroupOpen}
            >
              Editar grupo
            </Button>
            <Button
              colorScheme="brand"
              onClick={openCreate}
            >
              Adicionar produto
            </Button>
          </Flex>
        </Flex>
        {!isLoading && (products || []).length === 0 ? (
          <Text>Nenhum produto cadastrado.</Text>
        ) : (
          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing={4}
          >
            {(products || []).map((p) => (
              <ChakraCard
                key={p.id}
                variant="outline"
                overflow="hidden"
              >
                {p.imageUrl || p.imageLowUrl ? (
                  <Image
                    src={p.imageUrl ?? p.imageLowUrl ?? undefined}
                    alt={p.name}
                    h="130px"
                    w="100%"
                    objectFit="cover"
                  />
                ) : (
                  <Box
                    h="130px"
                    bg="gray.100"
                  />
                )}
                <CardBody>
                  <Flex
                    justify="space-between"
                    align="start"
                    gap={2}
                  >
                    <Heading
                      size="sm"
                      noOfLines={2}
                    >
                      {p.name}
                    </Heading>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Editar"
                        icon={<EditIcon />}
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(p.id)}
                      />
                      <IconButton
                        aria-label="Remover"
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => remove(p.id)}
                      />
                    </HStack>
                  </Flex>
                  <Text
                    fontWeight="bold"
                    mt={1}
                  >
                    {formatPrice(p.price)}
                  </Text>
                  <HStack
                    mt={2}
                    spacing={2}
                    wrap="wrap"
                  >
                    <Badge>Estoque: {p.stock}</Badge>
                    {(p.splits || []).length ? <Badge colorScheme="purple">+{sumPct(p)}% split</Badge> : null}
                  </HStack>
                </CardBody>
              </ChakraCard>
            ))}
          </SimpleGrid>
        )}
      </Card>
      {isOpen ? (
        <ProductFormModal
          isOpen={isOpen}
          onClose={onClose}
          groupId={groupId!}
          productId={editingId}
        />
      ) : null}
      {isGroupOpen ? (
        <GroupFormModal
          isOpen={isGroupOpen}
          onClose={onGroupClose}
          groupId={groupId}
        />
      ) : null}
    </Box>
  );
}
